// Senior Wellness — dedicated per-dog wellness page.
//
// Four sections, top → bottom:
//   1. Today's quick log (one-tap upsert). Sliders for exercise tolerance,
//      appetite, interest, mobility, pain. Good/OK/Bad day pill.
//   2. Trend charts using Recharts (line chart per metric).
//   3. Quality-of-life check-ins (user-chosen frequency + history).
//   4. Static enrichment cards (Social / Exercise / Mental).
import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { useDogs } from "@/context/DogsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import EmptyDogState from "@/components/app/EmptyDogState";
import {
  Smile, Meh, Frown, Users, Activity, Brain, Trash2, ClipboardList, Mail, Heart, ShieldAlert, EyeOff, HeartCrack, AlertTriangle,
} from "lucide-react";

// -----------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------
const METRICS = [
  { key: "exercise_tolerance", label: "Exercise tolerance", stroke: "#1C3A63" },
  { key: "appetite", label: "Appetite", stroke: "#CC9C33" },
  { key: "interest", label: "Interest in activities", stroke: "#8FA37A" },
  { key: "mobility", label: "Mobility (walking / up-down)", stroke: "#A04A30" },
  { key: "pain_level", label: "Pain level", stroke: "#7A5FA0" },
];

const DAY_RATINGS = [
  { value: "good", label: "Good day", Icon: Smile, activeCls: "bg-primary text-primary-foreground border-primary" },
  { value: "ok", label: "OK", Icon: Meh, activeCls: "bg-secondary text-secondary-foreground border-secondary" },
  { value: "bad", label: "Tough day", Icon: Frown, activeCls: "bg-accent text-accent-foreground border-accent" },
];

const ENRICHMENT = [
  {
    Icon: Users,
    title: "Social stimulation",
    accent: "text-primary",
    lines: [
      "Coffee walk with a calm dog friend.",
      "Kids or grandkids sit on the floor for gentle pets.",
      "Short car ride to a low-key errand.",
    ],
  },
  {
    Icon: Activity,
    title: "Exercise at their pace",
    accent: "text-accent-foreground",
    lines: [
      "Sniffing walks — pace set by the dog, not the clock.",
      "Balance work on a low, stable surface.",
      "Warm-water hydro or leisurely swim (if joints allow).",
    ],
  },
  {
    Icon: Brain,
    title: "Mental enrichment",
    accent: "text-primary",
    lines: [
      "Rotate a fresh food puzzle every few days.",
      "Scent games — hide kibble around the living room.",
      "New route: park, garden centre, or a friend's yard.",
    ],
  },
];

// -----------------------------------------------------------------------
// Utility bits
// -----------------------------------------------------------------------
const emptyForm = (dateISO) => ({
  log_date: dateISO,
  day_rating: "good",
  exercise_tolerance: 3,
  appetite: 3,
  interest: 3,
  mobility: 3,
  pain_level: 2,
  notes: "",
});

function ScoreSlider({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground uppercase tracking-wider">{label}</span>
        <span className="font-mono text-foreground tabular-nums">{value} / 5</span>
      </div>
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary cursor-pointer"
        data-testid={testid}
      />
    </div>
  );
}

// =======================================================================
// Section 1 — Today's quick log
// =======================================================================
function TodayLogSection({ dogId, todayLog, onSaved }) {
  const today = format(new Date(), "yyyy-MM-dd");
  const [form, setForm] = useState(todayLog ? { ...todayLog } : emptyForm(today));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(todayLog ? { ...todayLog } : emptyForm(today));
  }, [todayLog, today]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        log_date: form.log_date || today,
        day_rating: form.day_rating,
        exercise_tolerance: form.exercise_tolerance,
        appetite: form.appetite,
        interest: form.interest,
        mobility: form.mobility,
        pain_level: form.pain_level,
        notes: form.notes || "",
      };
      await api.post(`/dogs/${dogId}/senior/logs`, payload);
      toast.success(todayLog ? "Log updated" : "Today logged");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="senior-today-log">
      <CardContent className="p-6 sm:p-8 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {today}
            </p>
            <h3 className="font-display text-xl font-bold tracking-tight">Today&apos;s check-in</h3>
          </div>
          <div className="flex gap-1.5" data-testid="day-rating-picker">
            {DAY_RATINGS.map((r) => {
              const active = form.day_rating === r.value;
              const Icon = r.Icon;
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setForm({ ...form, day_rating: r.value })}
                  className={`flex items-center gap-1.5 border rounded-full px-3 py-1.5 text-xs transition-colors ${
                    active ? r.activeCls : "border-border text-muted-foreground hover:bg-secondary/50"
                  }`}
                  data-testid={`day-rating-${r.value}`}
                >
                  <Icon size={13} />
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {METRICS.map((m) => (
            <ScoreSlider
              key={m.key}
              label={m.label}
              value={form[m.key]}
              onChange={(v) => setForm({ ...form, [m.key]: v })}
              testid={`slider-${m.key}`}
            />
          ))}
        </div>

        <div>
          <Label htmlFor="today-notes" className="text-xs uppercase tracking-wider text-muted-foreground">
            Observations (optional)
          </Label>
          <Textarea
            id="today-notes"
            rows={2}
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. Slower on the stairs this morning. Ate all of dinner."
            className="mt-1.5 text-sm"
            data-testid="today-notes"
          />
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-muted-foreground">
            {todayLog ? "You already logged today — tweak anything and save to update." : "One quick save per day. Comes back with a chart in a couple of days."}
          </p>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="senior-save-today"
          >
            {saving ? "Saving…" : todayLog ? "Update today" : "Save today"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// =======================================================================
// Section 2 — Trend charts
// =======================================================================
const RANGES = [
  { value: 14, label: "14 d" },
  { value: 30, label: "30 d" },
  { value: 90, label: "90 d" },
];

function ChartsSection({ dogId, logs, onRangeChange, days }) {
  const chartData = useMemo(() => {
    return logs.map((row) => ({
      date: row.log_date.slice(5), // MM-DD
      exercise_tolerance: row.exercise_tolerance,
      appetite: row.appetite,
      interest: row.interest,
      mobility: row.mobility,
      pain_level: row.pain_level,
    }));
  }, [logs]);

  const goodBadCounts = useMemo(() => {
    let good = 0, ok = 0, bad = 0;
    for (const row of logs) {
      if (row.day_rating === "good") good += 1;
      else if (row.day_rating === "bad") bad += 1;
      else ok += 1;
    }
    return { good, ok, bad, total: logs.length };
  }, [logs]);

  return (
    <div className="space-y-4" data-testid="senior-charts-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionTitle overline="Trend" title="How the numbers have moved" />
        <div className="flex gap-1" data-testid="range-picker">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => onRangeChange(r.value)}
              className={`text-xs px-3 py-1.5 rounded-full border ${
                days === r.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/50"
              }`}
              data-testid={`range-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Good/OK/Bad summary chips */}
      <div className="flex flex-wrap gap-2" data-testid="day-summary-chips">
        <span className="inline-flex items-center gap-1.5 text-xs bg-primary/10 text-primary rounded-full px-3 py-1.5">
          <Smile size={12} /> {goodBadCounts.good} good
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs bg-secondary/60 text-secondary-foreground rounded-full px-3 py-1.5">
          <Meh size={12} /> {goodBadCounts.ok} ok
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs bg-accent/40 text-accent-foreground rounded-full px-3 py-1.5">
          <Frown size={12} /> {goodBadCounts.bad} tough
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground px-3 py-1.5">
          from {goodBadCounts.total} {goodBadCounts.total === 1 ? "log" : "logs"}
        </span>
      </div>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-4 sm:p-5">
          {chartData.length < 2 ? (
            <p className="text-sm text-muted-foreground text-center py-14">
              Log at least two days to see a trend. Come back tomorrow.
            </p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {METRICS.map((m) => (
                    <Line
                      key={m.key}
                      type="monotone"
                      dataKey={m.key}
                      name={m.label}
                      stroke={m.stroke}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =======================================================================
// Section 3 — Quality-of-life check-ins
// =======================================================================
const QOL_FIELDS = [
  { key: "still_enjoys",     label: "Still enjoys",                  Icon: Heart,        placeholder: "e.g. Squeaky toys, garden sniffs, the couch after dinner" },
  { key: "afraid_of",         label: "Afraid of / dislikes",           Icon: ShieldAlert,  placeholder: "e.g. Slippery floors, thunderstorms, being alone" },
  { key: "no_longer_afraid",  label: "No longer bothered by",          Icon: EyeOff,       placeholder: "e.g. Fireworks (hearing loss), the vacuum" },
  { key: "no_longer_enjoys",  label: "Used to love, but not anymore",  Icon: HeartCrack,   placeholder: "e.g. Long walks, tug, jumping onto the bed" },
];

function QoLSection({ dogId, checkins, settings, onSettingsChanged, onCheckinsChanged }) {
  const [form, setForm] = useState({
    still_enjoys: "", afraid_of: "", no_longer_afraid: "", no_longer_enjoys: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [freq, setFreq] = useState(settings.frequency_days);

  useEffect(() => setFreq(settings.frequency_days), [settings.frequency_days]);

  const nextDueLabel = useMemo(() => {
    if (checkins.length === 0) return "Never done — worth a first pass.";
    const last = parseISO(checkins[0].checkin_date);
    const daysSince = differenceInCalendarDays(new Date(), last);
    const daysLeft = settings.frequency_days - daysSince;
    if (daysLeft <= 0) {
      return `Last check-in was ${daysSince} day${daysSince === 1 ? "" : "s"} ago — you're due.`;
    }
    return `Last check-in ${daysSince} day${daysSince === 1 ? "" : "s"} ago · next one in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.`;
  }, [checkins, settings.frequency_days]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/dogs/${dogId}/senior/qol`, form);
      setForm({ still_enjoys: "", afraid_of: "", no_longer_afraid: "", no_longer_enjoys: "", notes: "" });
      onCheckinsChanged && onCheckinsChanged();
      toast.success("Check-in saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const saveFrequency = async () => {
    const clamped = Math.max(3, Math.min(120, Number(freq) || 14));
    try {
      await api.put(`/dogs/${dogId}/senior/qol/settings`, { frequency_days: clamped });
      onSettingsChanged && onSettingsChanged();
      toast.success(`Reminder cadence: every ${clamped} days`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/dogs/${dogId}/senior/qol/${id}`);
      onCheckinsChanged && onCheckinsChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="qol-section">
      <SectionTitle overline="Bigger picture" title="Quality of life" />
      <p className="text-sm text-muted-foreground max-w-2xl -mt-2">
        Every few weeks, check in on the shape of your dog&apos;s days. Some fears fade with hearing loss.
        Some old favourites quietly slip away. Writing it down helps you notice.
      </p>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm" data-testid="qol-next-due">{nextDueLabel}</p>
            <div className="flex items-center gap-2">
              <Label htmlFor="qol-freq" className="text-xs uppercase tracking-wider text-muted-foreground">
                Check in every
              </Label>
              <Input
                id="qol-freq"
                type="number"
                min={3}
                max={120}
                value={freq}
                onChange={(e) => setFreq(e.target.value)}
                className="w-20 h-9 text-sm"
                data-testid="qol-frequency-input"
              />
              <span className="text-sm text-muted-foreground">days</span>
              <Button
                type="button"
                onClick={saveFrequency}
                variant="outline"
                size="sm"
                data-testid="qol-frequency-save"
              >
                Save
              </Button>
            </div>
          </div>

          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="qol-form">
            {QOL_FIELDS.map((f) => {
              const Icon = f.Icon;
              return (
                <div key={f.key}>
                  <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
                    <Icon size={12} /> {f.label}
                  </Label>
                  <Textarea
                    rows={2}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="mt-1.5 text-sm"
                    data-testid={`qol-${f.key}`}
                  />
                </div>
              );
            })}
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything else worth remembering about this snapshot in time…"
                className="mt-1.5 text-sm"
                data-testid="qol-notes"
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="qol-submit"
              >
                {saving ? "Saving…" : "Save check-in"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* History */}
      {checkins.length > 0 ? (
        <div className="space-y-2" data-testid="qol-history">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            History ({checkins.length})
          </p>
          {checkins.map((c) => (
            <Card
              key={c.id}
              className="border-border shadow-none rounded-lg"
              data-testid={`qol-item-${c.id}`}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {c.checkin_date}
                    </p>
                    <div className="mt-2 space-y-1 text-sm text-foreground/85">
                      {QOL_FIELDS.map((f) => (c[f.key] ? (
                        <p key={f.key}><strong>{f.label}:</strong> {c[f.key]}</p>
                      ) : null))}
                      {c.notes ? <p className="text-muted-foreground italic mt-2">{c.notes}</p> : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(c.id)}
                    className="text-muted-foreground hover:text-destructive p-1 shrink-0"
                    title="Remove"
                    data-testid={`qol-remove-${c.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// =======================================================================
// Section 4 — Enrichment cards (static)
// =======================================================================
function EnrichmentSection() {
  return (
    <div className="space-y-4" data-testid="enrichment-section">
      <SectionTitle overline="Keep them lit up" title="Enrichment ideas" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ENRICHMENT.map((card) => {
          const Icon = card.Icon;
          return (
            <Card
              key={card.title}
              className="border-border shadow-none rounded-lg"
              data-testid={`enrichment-card-${card.title.split(" ")[0].toLowerCase()}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-9 w-9 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center">
                    <Icon size={16} />
                  </div>
                  <p className="font-display text-base font-bold tracking-tight">{card.title}</p>
                </div>
                <ul className="space-y-1.5 text-sm text-foreground/85">
                  {card.lines.map((line) => (
                    <li key={line} className="flex gap-2">
                      <span className="text-primary shrink-0">·</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// =======================================================================
// Alerts helper — pattern-detection over the last few daily logs
// =======================================================================
// Rules (kept intentionally strict so the banner doesn't cry wolf):
//   • Mobility dip   — the 3 most recent consecutive-day logs each ≤ 2.
//   • Pain spike     — the 3 most recent consecutive-day logs each ≥ 4.
// Both require actual consecutive dates so a single bad Sunday after a
// Tuesday log doesn't fire the alert.
function isConsecutive(logs) {
  for (let i = 1; i < logs.length; i += 1) {
    const prev = parseISO(logs[i - 1].log_date);
    const curr = parseISO(logs[i].log_date);
    if (differenceInCalendarDays(curr, prev) !== 1) return false;
  }
  return true;
}

function computeSeniorAlerts(allLogs, thresholds) {
  const mobilityMax = Number(thresholds?.mobility_threshold ?? 2);
  const painMin = Number(thresholds?.pain_threshold ?? 4);
  const need = Math.max(2, Math.min(7, Number(thresholds?.consecutive_days ?? 3)));
  if (!Array.isArray(allLogs) || allLogs.length < need) return [];
  // Logs come from the API in ascending date order — take the last N.
  const lastN = allLogs.slice(-need);
  if (!isConsecutive(lastN)) return [];

  const alerts = [];
  if (lastN.every((l) => Number(l.mobility) <= mobilityMax)) {
    const avg = (lastN.reduce((s, l) => s + Number(l.mobility), 0) / need).toFixed(1);
    alerts.push({
      id: "mobility-dip",
      title: `Mobility has been low for ${need} days in a row`,
      body: `Getting up, walking, stairs — averaging ${avg}/5 (threshold ≤ ${mobilityMax}). Worth a quick chat with your vet to rule out pain, joint changes, or a medication tweak.`,
    });
  }
  if (lastN.every((l) => Number(l.pain_level) >= painMin)) {
    const avg = (lastN.reduce((s, l) => s + Number(l.pain_level), 0) / need).toFixed(1);
    alerts.push({
      id: "pain-spike",
      title: `Pain has felt high for ${need} days in a row`,
      body: `Pain averaging ${avg}/5 (threshold ≥ ${painMin}) across ${need} consecutive days. Not something to sit on — call your vet.`,
    });
  }
  return alerts;
}

function SeniorAlertsBanner({ alerts }) {
  if (alerts.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="senior-alerts-banner">
      {alerts.map((a) => (
        <div
          key={a.id}
          className="flex items-start gap-3 rounded-lg border border-accent bg-accent/25 px-4 py-3"
          role="alert"
          data-testid={`senior-alert-${a.id}`}
        >
          <div className="h-8 w-8 rounded-md bg-accent text-accent-foreground flex items-center justify-center shrink-0">
            <AlertTriangle size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display font-bold text-sm text-foreground">{a.title}</p>
            <p className="mt-0.5 text-sm text-foreground/80 leading-relaxed">{a.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// =======================================================================
// Alert threshold controls — per-dog tuning so mellow / stoic dogs don't
// keep tripping the banner (and a naturally-energetic dog gets nudged sooner).
// =======================================================================
function AlertThresholdsSection({ dogId, settings, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const save = async () => {
    // Client-side clamp so a typed 6 or 0 becomes 5/1 before hitting the API.
    // Backend also validates (ge/le), but this way the user never sees a
    // silent rejection.
    const mob = Math.max(1, Math.min(5, Number(form.mobility_threshold) || 2));
    const pain = Math.max(1, Math.min(5, Number(form.pain_threshold) || 4));
    const days = Math.max(2, Math.min(7, Number(form.consecutive_days) || 3));
    if (
      Number(form.mobility_threshold) !== mob ||
      Number(form.pain_threshold) !== pain ||
      Number(form.consecutive_days) !== days
    ) {
      // Reflect the clamp visually so it's not a silent correction either.
      setForm({ mobility_threshold: mob, pain_threshold: pain, consecutive_days: days });
    }
    setSaving(true);
    try {
      await api.put(`/dogs/${dogId}/senior/alert-settings`, {
        mobility_threshold: mob,
        pain_threshold: pain,
        consecutive_days: days,
      });
      toast.success("Alert thresholds updated");
      setOpen(false);
      onSaved();
    } catch (err) {
      const raw = formatApiErrorDetail(err?.response?.data?.detail);
      const msg = typeof raw === "string" && raw ? raw : "Could not save thresholds";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="alert-thresholds-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Fine-tune</p>
            <p className="font-display text-lg font-bold tracking-tight">Alert thresholds</p>
            <p className="text-sm text-muted-foreground mt-1">
              Currently: mobility ≤ <strong>{settings.mobility_threshold}</strong> · pain ≥ <strong>{settings.pain_threshold}</strong> · <strong>{settings.consecutive_days}</strong> days in a row.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen((s) => !s)}
            data-testid="alert-thresholds-toggle"
          >
            {open ? "Cancel" : "Adjust"}
          </Button>
        </div>
        {open ? (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="alert-thresholds-form">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Mobility ≤</Label>
              <Input
                type="number"
                min={1}
                max={5}
                value={form.mobility_threshold}
                onChange={(e) => setForm({ ...form, mobility_threshold: e.target.value })}
                className="mt-1.5"
                data-testid="mobility-threshold-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Fires when mobility stays at or below this number.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Pain ≥</Label>
              <Input
                type="number"
                min={1}
                max={5}
                value={form.pain_threshold}
                onChange={(e) => setForm({ ...form, pain_threshold: e.target.value })}
                className="mt-1.5"
                data-testid="pain-threshold-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Fires when pain stays at or above this number.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Days in a row</Label>
              <Input
                type="number"
                min={2}
                max={7}
                value={form.consecutive_days}
                onChange={(e) => setForm({ ...form, consecutive_days: e.target.value })}
                className="mt-1.5"
                data-testid="consecutive-days-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Consecutive logged days needed.</p>
            </div>
            <div className="sm:col-span-3 flex justify-end">
              <Button
                onClick={save}
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="alert-thresholds-save"
              >
                {saving ? "Saving…" : "Save thresholds"}
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// =======================================================================
// Page
// =======================================================================
export default function SeniorWellnessPage() {
  const { activeDog } = useDogs();
  const [logs, setLogs] = useState([]);
  const [days, setDays] = useState(30);
  const [checkins, setCheckins] = useState([]);
  const [settings, setSettings] = useState({ frequency_days: 14 });
  const [alertSettings, setAlertSettings] = useState({ mobility_threshold: 2, pain_threshold: 4, consecutive_days: 3 });
  const [sending, setSending] = useState(false);

  const dogId = activeDog?.id;

  const loadLogs = useCallback(async () => {
    if (!dogId) return;
    try {
      const { data } = await api.get(`/dogs/${dogId}/senior/logs`, { params: { days } });
      setLogs(data.items || []);
    } catch {
      setLogs([]);
    }
  }, [dogId, days]);

  const loadCheckins = useCallback(async () => {
    if (!dogId) return;
    try {
      const { data } = await api.get(`/dogs/${dogId}/senior/qol`);
      setCheckins(data.items || []);
    } catch {
      setCheckins([]);
    }
  }, [dogId]);

  const loadSettings = useCallback(async () => {
    if (!dogId) return;
    try {
      const { data } = await api.get(`/dogs/${dogId}/senior/qol/settings`);
      setSettings({ frequency_days: data.frequency_days || 14 });
    } catch {
      setSettings({ frequency_days: 14 });
    }
  }, [dogId]);

  const loadAlertSettings = useCallback(async () => {
    if (!dogId) return;
    try {
      const { data } = await api.get(`/dogs/${dogId}/senior/alert-settings`);
      setAlertSettings({
        mobility_threshold: data.mobility_threshold || 2,
        pain_threshold: data.pain_threshold || 4,
        consecutive_days: data.consecutive_days || 3,
      });
    } catch {
      setAlertSettings({ mobility_threshold: 2, pain_threshold: 4, consecutive_days: 3 });
    }
  }, [dogId]);

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { loadCheckins(); }, [loadCheckins]);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadAlertSettings(); }, [loadAlertSettings]);

  const today = format(new Date(), "yyyy-MM-dd");
  const todayLog = useMemo(() => logs.find((l) => l.log_date === today), [logs, today]);
  const alerts = useMemo(() => computeSeniorAlerts(logs, alertSettings), [logs, alertSettings]);

  const sendRecapTest = async () => {
    setSending(true);
    try {
      await api.post(`/senior/recap/send-test`);
      toast.success("Sunday recap sent to your inbox");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send recap");
    } finally {
      setSending(false);
    }
  };

  if (!activeDog) {
    return <EmptyDogState />;
  }

  return (
    <div className="space-y-8" data-testid="senior-wellness-page">
      <SectionTitle
        overline="Every day counts"
        title="Senior Wellness"
        action={
          <Button
            variant="outline"
            onClick={sendRecapTest}
            disabled={sending}
            data-testid="senior-send-recap"
          >
            <Mail size={14} className="mr-1.5" /> {sending ? "Sending…" : "Email this week's recap"}
          </Button>
        }
      />
      <p className="text-sm text-muted-foreground max-w-2xl -mt-4">
        Small daily check-ins so you notice the drift — the good weeks and the harder ones.
        Log <strong>{activeDog.name}</strong> once a day and the trend does the work for you.
      </p>

      <SeniorAlertsBanner alerts={alerts} />

      <AlertThresholdsSection
        dogId={dogId}
        settings={alertSettings}
        onSaved={loadAlertSettings}
      />

      <TodayLogSection dogId={dogId} todayLog={todayLog} onSaved={loadLogs} />

      <ChartsSection dogId={dogId} logs={logs} days={days} onRangeChange={setDays} />

      <QoLSection
        dogId={dogId}
        checkins={checkins}
        settings={settings}
        onSettingsChanged={loadSettings}
        onCheckinsChanged={loadCheckins}
      />

      <EnrichmentSection />
    </div>
  );
}
